import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../utils/cn";

const textareaVariants = cva(
  "resize-none border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex field-sizing-content min-h-16 w-full rounded-md border bg-input-background px-3 py-2 text-base transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  {
    variants: {
      error: {
        true: "border-destructive focus-visible:ring-destructive/20",
      },
    },
  },
);

export interface TextareaProps
  extends
    React.ComponentProps<"textarea">,
    VariantProps<typeof textareaVariants> {
  /** Optional character limit to display a counter */
  showCounter?: boolean;
  /** Optional class name for the counter div */
  counterClassName?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      showCounter,
      counterClassName,
      maxLength,
      value,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const charCount = String(value ?? defaultValue ?? "").length;

    return (
      <div className="relative w-full group/textarea">
        <textarea
          ref={ref}
          data-slot="textarea"
          className={cn(
            textareaVariants({ error, className }),
            showCounter && "pb-7", // Extra padding to not overlap with counter
          )}
          value={value}
          defaultValue={defaultValue}
          maxLength={maxLength}
          {...props}
        />
        {showCounter && maxLength && (
          <div
            className={cn(
              "absolute right-2.5 bottom-1.5 px-1.5 py-0.5 text-2xs font-medium tracking-tight pointer-events-none transition-colors rounded-md bg-white/50 backdrop-blur-[1px]",
              charCount >= maxLength
                ? "text-destructive font-semibold"
                : "text-muted-foreground/70",
              counterClassName,
            )}
          >
            {charCount.toLocaleString()} / {maxLength.toLocaleString()}
          </div>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
